import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { customFieldsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, GripVertical, Settings2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
];

const ENTITY_TYPES = [
  { value: 'campaign', label: 'Campaigns' },
  { value: 'advertiser', label: 'Advertisers' },
  { value: 'report', label: 'Reports' },
];

const CustomFields = () => {
  const [activeTab, setActiveTab] = useState('campaign');
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    field_type: 'text',
    entity_type: 'campaign',
    required: false,
    placeholder: '',
    default_value: '',
    options: [],
  });
  const [newOption, setNewOption] = useState({ label: '', value: '' });

  const fetchFields = async () => {
    setLoading(true);
    try {
      const response = await customFieldsAPI.getAll(activeTab);
      setFields(response.data || []);
    } catch (error) {
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, [activeTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.label) {
      toast.error('Name and label are required');
      return;
    }

    try {
      const data = {
        ...formData,
        entity_type: activeTab,
        options: formData.field_type === 'dropdown' ? formData.options : null,
      };

      if (editingField) {
        await customFieldsAPI.update(editingField.id, data);
        toast.success('Custom field updated');
      } else {
        await customFieldsAPI.create(data);
        toast.success('Custom field created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom field');
    }
  };

  const handleDelete = async (fieldId) => {
    if (!confirm('Are you sure you want to delete this custom field?')) return;
    
    try {
      await customFieldsAPI.delete(fieldId);
      toast.success('Custom field deleted');
      fetchFields();
    } catch (error) {
      toast.error('Failed to delete custom field');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      field_type: 'text',
      entity_type: activeTab,
      required: false,
      placeholder: '',
      default_value: '',
      options: [],
    });
    setNewOption({ label: '', value: '' });
    setEditingField(null);
  };

  const openEditDialog = (field) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      label: field.label,
      field_type: field.field_type,
      entity_type: field.entity_type,
      required: field.required,
      placeholder: field.placeholder || '',
      default_value: field.default_value || '',
      options: field.options || [],
    });
    setDialogOpen(true);
  };

  const addOption = () => {
    if (!newOption.label || !newOption.value) return;
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { ...newOption }]
    }));
    setNewOption({ label: '', value: '' });
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Custom Fields</h1>
            <p className="text-sm text-slate-500 mt-1">
              Define custom fields for campaigns, advertisers, and reports
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-custom-field-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Field
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Field Name (ID)</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        name: e.target.value.toLowerCase().replace(/\s+/g, '_')
                      }))}
                      placeholder="e.g., region"
                      disabled={!!editingField}
                    />
                  </div>
                  <div>
                    <Label>Display Label</Label>
                    <Input
                      value={formData.label}
                      onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g., Region"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Field Type</Label>
                  <Select
                    value={formData.field_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, field_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.field_type === 'dropdown' && (
                  <div className="space-y-2">
                    <Label>Dropdown Options</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Label"
                        value={newOption.label}
                        onChange={(e) => setNewOption(prev => ({ ...prev, label: e.target.value }))}
                      />
                      <Input
                        placeholder="Value"
                        value={newOption.value}
                        onChange={(e) => setNewOption(prev => ({ ...prev, value: e.target.value }))}
                      />
                      <Button type="button" variant="outline" onClick={addOption}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.options.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {formData.options.map((opt, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-slate-50 p-2 rounded">
                            <span className="flex-1 text-sm">{opt.label} ({opt.value})</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOption(idx)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Placeholder</Label>
                    <Input
                      value={formData.placeholder}
                      onChange={(e) => setFormData(prev => ({ ...prev, placeholder: e.target.value }))}
                      placeholder="Optional placeholder text"
                    />
                  </div>
                  <div>
                    <Label>Default Value</Label>
                    <Input
                      value={formData.default_value}
                      onChange={(e) => setFormData(prev => ({ ...prev, default_value: e.target.value }))}
                      placeholder="Optional default"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="required"
                    checked={formData.required}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
                  />
                  <Label htmlFor="required" className="cursor-pointer">Required field</Label>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingField ? 'Update' : 'Create'} Field
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="border-b px-4">
                <TabsList className="bg-transparent">
                  {ENTITY_TYPES.map(entity => (
                    <TabsTrigger
                      key={entity.value}
                      value={entity.value}
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-slate-900 rounded-none"
                    >
                      {entity.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              
              {ENTITY_TYPES.map(entity => (
                <TabsContent key={entity.value} value={entity.value} className="p-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : fields.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Settings2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No custom fields defined for {entity.label.toLowerCase()}</p>
                      <p className="text-sm mt-1">Click "Add Field" to create one</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {fields.map((field, index) => (
                        <div
                          key={field.id}
                          className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200"
                        >
                          <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                          <div className="flex-1">
                            <div className="font-medium text-slate-900">{field.label}</div>
                            <div className="text-sm text-slate-500">
                              {field.name} • {FIELD_TYPES.find(t => t.value === field.field_type)?.label}
                              {field.required && <span className="text-red-500 ml-2">*Required</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(field)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(field.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CustomFields;
